import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import MarketingNav from "@/components/layout/MarketingNav";
import { CheckCircle2, Loader2, XCircle, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", label: "", amount: 0 });
  const attempts = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", label: "Missing session_id" });
      return;
    }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", label: data.label, amount: data.amount_total });
          toast.success("Payment successful — your subscription is active.");
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", label: data.label });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", label: data.label });
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (err) {
        setState({ status: "error", label: err.response?.data?.detail || "Failed to verify payment" });
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-stone-50">
      <MarketingNav />
      <section className="mx-auto max-w-2xl px-6 py-24">
        <div className="card-flat text-center">
          {state.status === "checking" && (
            <>
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-slate-950" />
              <h1 className="mt-6 font-display text-2xl font-medium text-slate-950">Verifying your payment…</h1>
              <p className="mt-2 text-sm text-stone-600">This usually takes a few seconds.</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" strokeWidth={1.5} />
              <div className="label-mono mt-4">Payment Confirmed</div>
              <h1 className="mt-2 font-display text-3xl font-bold text-slate-950">You're in.</h1>
              <p className="mt-3 text-sm text-stone-600">{state.label} — <span className="font-mono">${(state.amount / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></p>
              <div className="mt-8 flex justify-center gap-3">
                <Link to="/app" className="btn-primary">Enter ATLAS™ <ArrowUpRight className="h-4 w-4" /></Link>
                <Link to="/" className="btn-secondary">Home</Link>
              </div>
            </>
          )}
          {["expired", "timeout", "error"].includes(state.status) && (
            <>
              <XCircle className="mx-auto h-12 w-12 text-red-500" strokeWidth={1.5} />
              <h1 className="mt-4 font-display text-2xl font-medium text-slate-950">Payment not confirmed</h1>
              <p className="mt-2 text-sm text-stone-600">{state.label || "Please contact support if you were charged."}</p>
              <div className="mt-8 flex justify-center gap-3">
                <button onClick={() => navigate("/#pricing")} className="btn-secondary">Return to Pricing</button>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
